import {addDays, addMinutes, format, startOfWeek} from "date-fns";
import {RRule} from "rrule";

export type Availability = {
  recurrenceRule: string;
  durationInMinutes: number;
  onlineBooking: boolean;
};

export type AvailabilityRules = {
  monday: Availability[];
  tuesday: Availability[];
  wednesday: Availability[];
  thursday: Availability[];
  friday: Availability[];
  saturday: Availability[];
  sunday: Availability[];
};

function weeklyRecurrence(start: Date, durationInMinutes: number): Availability {
  const r = new RRule({
    freq: RRule.WEEKLY,
    interval: 1,
    dtstart: start,
  });
  return {
    recurrenceRule: r.toString(),
    durationInMinutes,
    onlineBooking: false,
  };
}

export function defaultAvailabilityRules(start: Date = new Date()): AvailabilityRules {
  const workdayStartHours = 8.5;
  const mondayStart = addMinutes(
    startOfWeek(start, {weekStartsOn: 1}),
    workdayStartHours * 60,
  );
  const durationInMinutes = 9 * 60;
  return {
    monday: [weeklyRecurrence(mondayStart, durationInMinutes)],
    tuesday: [weeklyRecurrence(addDays(mondayStart, 1), durationInMinutes)],
    wednesday: [weeklyRecurrence(addDays(mondayStart, 2), durationInMinutes)],
    thursday: [weeklyRecurrence(addDays(mondayStart, 3), durationInMinutes)],
    friday: [weeklyRecurrence(addDays(mondayStart, 4), durationInMinutes)],
    saturday: [],
    sunday: [],
  };
}

const WEEK_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
export type WeekDay = (typeof WEEK_DAYS)[number];

export type WorkingHoursSpan = {firstDay: WeekDay; lastDay: WeekDay; start: string; end: string};

/** The consecutive days sharing one availability window, or null when the week is not that regular. */
export function uniformWorkingHours(rules: AvailabilityRules): WorkingHoursSpan | null {
    const windows = WEEK_DAYS.map((day) => {
        const availabilities = rules[day];
        if (availabilities.length !== 1) return availabilities.length === 0 ? null : undefined;
        const start = RRule.fromString(availabilities[0].recurrenceRule).options.dtstart;
        return {day, start: format(start, "HH:mm"), end: format(addMinutes(start, availabilities[0].durationInMinutes), "HH:mm")};
    });
    if (windows.some((window) => window === undefined)) return null;
    const working = windows.filter((window): window is {day: WeekDay; start: string; end: string} => Boolean(window));
    if (working.length === 0) return null;
    const firstIndex = WEEK_DAYS.indexOf(working[0].day);
    const consecutive = working.every((window, index) => WEEK_DAYS.indexOf(window.day) === firstIndex + index);
    const sameHours = working.every((window) => window.start === working[0].start && window.end === working[0].end);
    if (!consecutive || !sameHours) return null;
    return {firstDay: working[0].day, lastDay: working[working.length - 1].day, start: working[0].start, end: working[0].end};
}
